import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { User, Mail, Phone, MapPin, Calendar, Edit2, Save, X, Camera } from 'lucide-react';
import { useForm } from 'react-hook-form';

interface ProfileForm {
  name: string;
  email: string;
  phone: string;
  bio: string;
  location: string;
}

const StudentProfile: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState<string>('');
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ProfileForm>({
    defaultValues: {
      name: 'Étudiant Dupont',
      email: 'student@lms-smdformation.com',
      phone: '+33 6 12 34 56 78',
      bio: 'Entraîneur de football passionné par l\'analyse des données et l\'optimisation des performances. Je cherche à me spécialiser dans l\'utilisation des technologies GPS pour améliorer les entraînements.',
      location: 'Paris, France'
    }
  });

  // Mock data
  const userStats = {
    joinedDate: '2024-01-01',
    coursesCompleted: 1,
    coursesEnrolled: 3,
    certificatesEarned: 1,
    totalStudyTime: 45,
    averageScore: 87
  };

  const achievements = [
    {
      id: 1,
      title: 'Premier Quiz Réussi',
      description: 'Félicitations pour votre premier quiz réussi !',
      icon: '🎯',
      earnedAt: '2024-01-10'
    },
    {
      id: 2,
      title: 'Étudiant Assidu',
      description: '10 heures d\'étude consécutives',
      icon: '📚',
      earnedAt: '2024-01-15'
    },
    {
      id: 3,
      title: 'Expert GPS',
      description: 'Cours GPS terminé avec mention',
      icon: '🏆',
      earnedAt: '2024-01-20'
    }
  ];

  const certificates = [
    {
      id: 1,
      title: 'Expert gestion GPS et analyse des données',
      issueDate: '2024-01-20',
      certificateId: 'FA-GPS-2024-001',
      status: 'valid'
    }
  ];

  const onSubmit = (data: ProfileForm) => {
    console.log('Profile update:', data);
    // Here you would make an API call to update the profile
    setIsEditing(false);
    alert('Profil mis à jour avec succès !');
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Mon Profil</h1>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Modifier le profil
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleSubmit(onSubmit)}
                className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <X className="h-4 w-4 mr-2" />
                Annuler
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Informations personnelles</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <div className="w-24 h-24 bg-brand-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {profileImage ? (
                        <img src={profileImage} alt="Profile" className="w-24 h-24 rounded-full object-cover" />
                      ) : (
                        'ED'
                      )}
                    </div>
                    {isEditing && (
                      <label className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-50">
                        <Camera className="h-4 w-4 text-gray-600" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Étudiant Dupont</h3>
                    <p className="text-gray-600">Membre depuis janvier 2024</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="inline h-4 w-4 mr-1" />
                      Nom complet
                    </label>
                    <input
                      {...register('name', { required: 'Le nom est requis' })}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-brand-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="inline h-4 w-4 mr-1" />
                      Email
                    </label>
                    <input
                      {...register('email', { 
                        required: 'L\'email est requis',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email invalide'
                        }
                      })}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-brand-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="inline h-4 w-4 mr-1" />
                      Téléphone
                    </label>
                    <input
                      {...register('phone')}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      Localisation
                    </label>
                    <input
                      {...register('location')}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    À propos de moi
                  </label>
                  <textarea
                    {...register('bio')}
                    disabled={!isEditing}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                  />
                </div>
              </form>
            </div>

            {/* Achievements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Réalisations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {achievements.map((achievement) => (
                  <div key={achievement.id} className="flex items-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-3xl mr-4">{achievement.icon}</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{achievement.title}</h3>
                      <p className="text-sm text-gray-600">{achievement.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Obtenu le {new Date(achievement.earnedAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certificates */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Certificats</h2>
              <div className="space-y-4">
                {certificates.map((certificate) => (
                  <div key={certificate.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">{certificate.title}</h3>
                      <p className="text-sm text-gray-600">ID: {certificate.certificateId}</p>
                      <p className="text-sm text-gray-500">
                        Délivré le {new Date(certificate.issueDate).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 rounded-full">
                        Valide
                      </span>
                      <button className="px-3 py-1 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 transition-colors">
                        Télécharger
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Stats Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Statistiques</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cours terminés</span>
                  <span className="font-semibold">{userStats.coursesCompleted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cours inscrits</span>
                  <span className="font-semibold">{userStats.coursesEnrolled}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Certificats</span>
                  <span className="font-semibold">{userStats.certificatesEarned}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Temps d'étude</span>
                  <span className="font-semibold">{userStats.totalStudyTime}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Score moyen</span>
                  <span className="font-semibold">{userStats.averageScore}%</span>
                </div>
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Informations du compte</h2>
              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Membre depuis</span>
                  <span className="ml-auto font-medium">
                    {new Date(userStats.joinedDate).toLocaleDateString('fr-FR', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <User className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Type de compte</span>
                  <span className="ml-auto font-medium">Étudiant</span>
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Sécurité</h2>
              <div className="space-y-3">
                <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  Changer le mot de passe
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  Authentification à deux facteurs
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  Sessions actives
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StudentProfile;